<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <b-card>
        <b-row>
          <b-col>
            <h3>{{ $dt('detection_set_file_upload.title', 'Upload files') }}</h3>
            <p>{{ $dt('detection_set_file_upload.description', 'To upload select directory containing detections. Each folder in directory will be separate detection. Each detection must contain json file and detection images') }}</p>
          </b-col>
        </b-row>
        <div class="text-center" v-if="loading">
          <b-spinner type="grow" variant="primary" />
        </div>
        <template v-else>
          <template v-if="!hasSchema">
            <b-alert show variant="danger">{{ $dt('detection_set_file_upload.create_schema_errror', 'Please create/attach json schema to detection set first') }}</b-alert>
          </template>
          <template v-else>
            <b-row>
              <b-col>
                <form ref="fileUpload" :class="['uploadFrom', { dragDropCapable: dragDropCapable, canDropNow: canDropNow }]"
                  @click="uploadClick()">
                  <input type="file" ref="fileUploadFallback" webkitdirectory directory multiple style="display: none" :disabled="isUploading" @change="onFallbackUpload">
                    <template v-if="!isUploading">
                      <div class="uploadHint" v-if="dragDropCapable">
                        <b-button>{{ $dt('detection_set_file_upload.hint_can_drag_filder', 'Drag and Drop the folder here or click to upload!') }}</b-button>
                      </div>
                      <div class="uploadHint" v-else>
                        <b-button>{{ $dt('detection_set_file_upload.hint_can_drag', 'Click to upload!') }}</b-button>
                      </div>
                    </template>
                    <div v-else class="text-center uploadHint">
                      <b-spinner variant="light"></b-spinner>
                    </div>
                </form>
              </b-col>
            </b-row>

            <b-button-group class="mb-2 btn-group-justified">
              <b-btn  @click="upload" variant="primary" :disabled="isUploading || detections.length === 0">{{ $dt('detection_set_file_upload.upload_btn', 'Upload') }}</b-btn>
              <b-btn  @click="clearAll" :variant="isUploading ? 'success' : 'secondary'" :disabled="(isUploading) || detections.length === 0">{{ $dt('detection_set_file_upload.clear_btn', 'Clear list') }}</b-btn>
            </b-button-group>

            <b-progress v-if="isUploading" class="mb-2" :max="uploadProgress.count" show-value>
              <b-progress-bar :value="uploadProgress.done" variant="success"></b-progress-bar>
              <b-progress-bar :value="uploadProgress.processing" variant="primary" animated></b-progress-bar>
            </b-progress>

            <b-table v-if="detections.length > 0" class="table-sm text-center" responsive outlined hover head-variant="light" :fields="tableFields"
                    :items="detections">
              <template v-slot:cell(files)="{ item }">
                <div>{{ item.json.fileName }}</div>
                <div v-for="(file, index) in item.files" :key="index">
                  {{ file.fileName }} ({{ file.human_readable_size }})
                </div>
              </template>
              <template v-slot:cell(status)="{ item }">
                <b-badge :variant="buttonVariant(item.status)">{{ item.status }}</b-badge>
                <div v-if="item.status === 'error'">
                  <b-alert class="small-alert" v-for="(error, key) in item.errors" :key="key" show variant="danger" small>
                    {{ key }} :
                    {{ error }}
                  </b-alert>
                </div>
              </template>

            </b-table>
          </template>
        </template>
      </b-card>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-underscore-dangle */
import { mapState } from 'vuex';
import Services from '@/services/index';

import '@riophae/vue-treeselect/dist/vue-treeselect.css';

class UploadQueue {
  constructor(concurrency) {
    this.concurrency = concurrency;
    this.queue = [];
    this.running = 0;
  }

  async enqueue(task) {
    return new Promise((resolve) => {
      const run = async () => {
        this.running++;
        await task();
        this.running--;

        if (this.queue.length > 0) {
          this.queue.shift()();
        } else if (this.running === 0) {
          resolve();
        }
      };

      if (this.running < this.concurrency) {
        run();
      } else {
        this.queue.push(run);
      }
    });
  }
}

export default {
  name: 'DetectionSetFileUpload',
  props: {
    id: { type: Number, default: null },
    disabled: { type: Boolean, default: false },
    defaultFileInfo: {
      type: Object,
      default() {
        return {

        };
      }
    },
  },
  data() {
    return {
      dragDropCapable: false,
      canDropNow: false,
      tmpUrls: {},
      detections: [],
      list: [],
      detectionFinal: null,
      loadedDetectionJson: null,
      isUploading: false,
      uploadFailedMessage: null,
      uploadConcurency: 1,
      tid: 0, // for unique file identification,
      schema: null,
      loading: true,
      tableFields: [
        // { key: 'name', label: this.$dt('detectin_upload.detection_name', 'Detection Name') },
        { key: 'folder', label: this.$dt('detectin_upload.detection_folder', 'Folder') },
        { key: 'files', label: this.$dt('detectin_upload.detection_files', 'Files') },
        { key: 'status', label: this.$dt('detectin_upload.errors', 'Status'), thStyle: { width: '300px' } },
      ],
    };
  },
  computed: {
    uploadProgress() {
      const ret = { count: 0, done: 0, processing: 0 };
      this.detections.forEach((detection) => {
        if (detection.status === 'complete') ret.done++;
        if (detection.status === 'uploading') ret.processing++;
        ret.count++;
      });

      return ret;
    },
    hasSchema() {
      return this.schema !== null;
    },
    ...mapState('Auth', ['activeProjectId']),
  },
  watch: {
    page: 'getData',
    // filters: { deep: true, handler: 'getData' },
    activeProjectId() {
      if (this.activeProjectId) this.getData();
    },
    uploadFailedMessage() {
      if (this.uploadFailedMessage) {
        this.alertError({ title: 'Upload error', text: this.uploadFailedMessage });
      }
    },
  },
  async mounted() {
    await this.load();
    if (this.hasSchema) {
      this.dragDropCapable = this.checkIfDragDropCapable();
      this.initDragDropCapability();
    }
  },
  methods: {
    load() {
      return Services.detectionSets.editData(this.activeProjectId, this.id).then((response) => {
        this.schema = response.data.detectionSet.json_schema;
      }).finally(() => {
        this.loading = false;
      });
    },
    clearAll() {
      this.list = [];
      this.detections = [];
      this.detectionFinal = null;
    },
    async loadDetectionJson(detection) {
      if (!detection.json) return false;

      const reader = new FileReader();
      reader.readAsText(detection.json.file);

      const result = new Promise((resolve, reject) => {
        reader.onloadend = () => {
          try {
            const json = JSON.parse(reader.result);
            resolve(json);
          } catch (e) {
            console.error(e);
            reject(e);
          }
        };
      });

      return result;
    },
    buttonVariant(status) {
      switch (status) {
        case 'prepared':
          return 'info';
        case 'uploading':
          return 'primary';
        case 'complete':
          return 'success';
        case 'error':
          return 'danger';
        default:
          return 'info';
      }
    },
    async upload() {
      this.isUploading = true;

      const uploadQueue = new UploadQueue(this.uploadConcurency);

      let done = 0;
      await Promise.all(
        this.detections.map(async (detection) => {
          detection.status = 'uploading';
          await uploadQueue.enqueue(async () => {
            await this.uploadDetection(detection);
            if (done === this.detections.length - 1) {
              this.isUploading = false;
            }
            done++;
          });
        })
      );
    },

    async uploadDetection(detection) {
      detection.errors = {};
      try {
        detection.status = 'uploading';

        const jsonData = await this.loadDetectionJson(detection);

        const resp = await Services.detections.uploadJson(this.activeProjectId, this.id, {
          data: JSON.stringify(jsonData),
          name: detection.name,
        });

        const detectionId = resp.data.detection_id;
        const files = detection.files.map((file) => ({ file: file.file }));

        await Promise.all(detection.files.map(async (file) => {
          await Services.detections.uploadFiles(this.activeProjectId, this.id, detectionId, { files: [file] });
        }));

        detection.status = 'complete';
      } catch (e) {
        detection.status = 'error';
        if (e?.errors?.json_data) {
          detection.errors = e.errors.json_data;
        }
        if (e?.errors) {
          this.uploadFailedMessage = e?.errors[0];
        }
      }
    },

    getDetectionName(file) {
      const name = file.name.split('.').shift();
      const jsonName = this.detectionJson.length ? this.detectionJson[0].file.name.split('.').shift() : null;
      if (jsonName && jsonName === name) return name;
      return `${name} (${jsonName})`;
    },

    addFileToList(file, path) {
      if (this.list.length > 1000) {
        console.warn('max files exceeded');
        return;
      }

      const splitPath = path.split('/');

      const fileName = file.name.lastIndexOf('/') !== -1
        ? file.name.substring(file.name.lastIndexOf('/') + 1)
        : file.name;

      this.list.push({
        path: path,
        folder: splitPath[splitPath.length - 2],
        file: file,
        fileName: fileName,
        extension: fileName.split('.').pop(),
      });
    },

    processFileList() {
      // create detections based on folder names
      const detections = [...this.detections];

      this.list.forEach((entry) => {
        let detection = detections.find((d) => d.folder === entry.folder);
        if (!detection) {
          detections.push({
            name: '',
            folder: entry.folder,
            files: [],
            json: null,
            errors: [],
            status: 'prepared',
          });

          detection = detections[detections.length - 1];
        }
        // single json file per detection but multiple images
        if (entry.extension === 'json') {
          detection.json = entry;
          detection.name = entry.fileName.split('.').shift();
        } else {
          entry.human_readable_size = this.formatBytes(entry.file.size);
          detection.files.push(entry);
        }
      });
      this.detections = detections.filter((d) => d.json && d.files.length > 0);
    },

    onFallbackUpload(e) {
      for (let i = 0; i < e.target.files.length; i++) {
        this.addFileToList(e.target.files[i], e.target.files[i]?.webkitRelativePath);
      }
      this.processFileList();
      this.$refs.fileUpload.reset();
    },
    checkIfDragDropCapable() {
      const div = document.createElement('div');
      return (('draggable' in div)
        || ('ondragstart' in div && 'ondrop' in div))
        && 'FormData' in window
        && 'FileReader' in window;
    },
    initDragDropCapability() {
      if (!this.dragDropCapable) return;

      ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach((evt) => {
        this.$refs.fileUpload.addEventListener(evt, (e) => {
          e.preventDefault();
          e.stopPropagation();
        }, false);
      });

      ['dragover', 'dragenter'].forEach((evt) => {
        this.$refs.fileUpload.addEventListener(evt, () => {
          if (this.disabled) return;
          this.canDropNow = true;
        });
      });

      ['dragleave', 'dragend', 'drop'].forEach((evt) => {
        this.$refs.fileUpload.addEventListener(evt, () => {
          this.canDropNow = false;
        });
      });

      this.$refs.fileUpload.addEventListener('drop', (e) => {
        if (this.disabled) return;
        const { items } = e.dataTransfer;
        if (items.length === 1 && items[0].webkitGetAsEntry().isDirectory) {
          // this allows to upload single folder rather than selecting all its contents
          const dirReader = items[0].webkitGetAsEntry().createReader();
          dirReader.readEntries((entries) => {
            for (let i = 0; i < entries.length; i++) {
              let path = entries[i]?.fullPath;
              if (entries[i].isFile) {
                path = path.split('/').slice(0, -1).join('/');
              }
              this.traverseFileTree(entries[i], path);
            }
          });
        } else {
          // if multiple folders
          for (let i = 0; i < items.length; i++) {
            const item = items[i].webkitGetAsEntry();
            // if file do nothing... we expect only folders
            if (item && item.isDirectory) {
              this.traverseFileTree(item, item?.fullPath);
            }
          }
        }

        this.processFileList();
      });
    },

    traverseFileTree(item, path) {
      path = path || '';
      if (item.isFile) {
        // Get file
        item.file((file) => {
          this.addFileToList(file, `${path}/`);
        });
      } else if (item.isDirectory) {
        // Get folder contents
        const dirReader = item.createReader();
        dirReader.readEntries((entries) => {
          for (let i = 0; i < entries.length; i++) {
            this.traverseFileTree(entries[i], path);
          }
        });
      }
    },
    uploadClick() {
      if (this.disabled) return;
      this.$refs.fileUploadFallback.click();
    },
    formatBytes(a, b = 2) {
      if (a === 0) return '0 Bytes'; const c = b < 0 ? 0 : b; const
        // eslint-disable-next-line no-restricted-properties
        d = Math.floor(Math.log(a) / Math.log(1024)); return `${parseFloat((a / Math.pow(1024, d)).toFixed(c))} ${['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'][d]}`;
    },

  },
  destroyed() {
    Object.values(this.tmpUrls).forEach((url) => URL.revokeObjectURL(url));
  }
};
</script>

<style scoped lang=scss>
.btn-group-justified {
  width: 100%;
}

.uploadFrom {
  display: block;
  min-height: 2.5em;
  background-color: $dark;
  border: solid 1px $light;
  margin: auto;
  margin-bottom: 0.5em;

  /* margin-top: 40px; */
  border-radius: 4px;
}

.uploadFrom .uploadHint {
  text-align: center;
  padding: 0.5em;
  font-size: 1.2em;
}

.file-row.unsavedFile {
  border-color: $green;
}

.file-row.deletedFile {
  border-color: $red;
  color: $red;
}

.uploadFrom.canDropNow {
  background-color: $green;
}

.small-alert {
  font-size: 0.8rem;
  padding: 0.2rem 1rem;
  margin-bottom: 0.5em;
}

.item-errors-wrap {
  max-height: 200px;
  overflow-y: auto;
}
</style>
